import {RouteObject, RouterContextProvider, redirect} from 'react-router';

import {supportsEndpoints} from 'src/containers/SupportClients/api';
import {
  EFeatureFlag,
  EPaymentType,
  EPermission,
  getTenantData,
  userEndpoints,
} from 'src/containers/User';
import PATHS from 'src/paths';
import {getDispatch, getState} from 'src/routerContext';

import {EPaymentTab, PAYMENT_TABS} from './definitions';

const checkReplenishmentAccess = async (
  context: RouterContextProvider,
  supportTenantId?: string,
) => {
  const dispatch = getDispatch(context);
  const {data: featureFlags} = await dispatch(userEndpoints.getFeatureFlags.initiate());

  if (!!featureFlags?.[EFeatureFlag.STRIPE_AUTOPAYMENT]) {
    return true;
  }

  if (supportTenantId) {
    const {data: supportClient} = await dispatch(
      supportsEndpoints.getSupportClient.initiate(supportTenantId),
    );

    return supportClient?.paymentType === EPaymentType.PREPAYMENT;
  }

  const {data: tenantData} = await dispatch(getTenantData.initiate());

  return tenantData?.paymentType === EPaymentType.PREPAYMENT;
};

const checkTransactionsAccess = async (
  context: RouterContextProvider,
  supportTenantId?: string,
) => {
  const permissions = getState(context).user?.permissions ?? [];

  const accessPermission = supportTenantId
    ? EPermission.PERMISSION_TENANT_TRANSACTION_READ
    : EPermission.PERMISSION_TRANSACTION_READ;

  return permissions.includes(accessPermission);
};

const accessHash: Partial<
  Record<string, (context: RouterContextProvider, supportTenantId?: string) => Promise<boolean>>
> = {
  [EPaymentTab.REPLENISHMENT]: checkReplenishmentAccess,
  [EPaymentTab.ACCOUNT_STATEMENT]: checkTransactionsAccess,
};

const getMainTab = async (context: RouterContextProvider, supportTenantId?: string) => {
  for (const tab of PAYMENT_TABS) {
    const checkAccess = accessHash[tab];

    if (!checkAccess) {
      return tab;
    }

    const hasAccess = await checkAccess(context, supportTenantId);

    if (hasAccess) {
      return tab;
    }
  }

  return null;
};

const mainTabLoader = async (context: RouterContextProvider, supportTenantId?: string) => {
  const mainTab = await getMainTab(context, supportTenantId);

  throw redirect(mainTab || PATHS.HOME);
};

const toMainTab = async (context: RouterContextProvider, supportTenantId?: string) => {
  const mainTab = await getMainTab(context, supportTenantId);

  throw redirect(mainTab ? `../${mainTab}` : PATHS.HOME);
};

const children: RouteObject[] = [
  {
    index: true,
    loader: async ({params, context}) => {
      const supportTenantId = params.id;

      await mainTabLoader(context, supportTenantId);
    },
  },
  {
    path: EPaymentTab.REPLENISHMENT,
    lazy: async () => ({
      Component: (await import('./PaymentReplenishment')).default,
    }),
    loader: async ({params, context}) => {
      const supportTenantId = params.id;
      const hasAccess = await checkReplenishmentAccess(context, supportTenantId);

      if (hasAccess) {
        return null;
      }

      await toMainTab(context, supportTenantId);
    },
  },
  {
    path: EPaymentTab.ACCOUNT_STATEMENT,
    lazy: async () => ({
      Component: (await import('src/containers/AccountStatement')).default,
    }),
    loader: async ({params, context}) => {
      const supportTenantId = params.id;
      const hasAccess = await checkTransactionsAccess(context, supportTenantId);

      if (hasAccess) {
        return null;
      }

      await toMainTab(context, supportTenantId);
    },
  },
  {
    path: EPaymentTab.DOCUMENTATION,
    lazy: async () => ({
      Component: (await import('./CompanyDocument')).default,
    }),
  },
];

export const paymentTabRoutes: RouteObject = {
  loader: async ({params, context}) => {
    const supportTenantId = params.id;
    const unavailableTabs: EPaymentTab[] = [];

    for (const tab of PAYMENT_TABS) {
      const checkAccess = accessHash[tab];

      if (!checkAccess) {
        continue;
      }

      const hasAccess = await checkAccess(context, supportTenantId);

      if (!hasAccess) {
        unavailableTabs.push(tab);
      }
    }

    return {
      tabs: PAYMENT_TABS.filter((tab) => !unavailableTabs.includes(tab)),
    };
  },
  children,
};
